/**
 * Yahoo Finance 報價服務
 * 作為 MIS 即時 API 的備援來源，並提供興櫃股票盤中報價
 *
 * 實測 (2026-08-27):
 * - 涵蓋上市 (.TW) / 上櫃 (.TWO) / 興櫃 (.TWO)、主動式 ETF (00981A)、特別股 (2887I)
 * - regularMarketPrice 盤中為現價、收盤後為當日收盤價
 * - chartPreviousClose 為昨日收盤價
 * - 無效代號回傳 HTTP 404 (乾淨的錯誤信號，不重試)
 * - 需透過 CORS 代理呼叫 (優先使用自建 Workers 代理，實測可用)
 */

import { logger } from '../utils/logger';
import { ErrorHandler } from '../utils/errorHandler';
import { RetryHelper } from '../utils/retryHelper';
import { stockDataCache } from '../utils/cacheService';
import corsProxyService from './corsProxyService';
import staticStockDataService from './staticStockDataService';

export interface YahooQuote {
  symbol: string; // 台股代號 (如 2330)
  yahooSymbol: string; // 完整代號 (如 2330.TW)
  currentPrice: number; // 當日最新價 (盤中=現價, 收盤後=當日收盤)
  previousClose: number; // 昨日收盤價
  change: number;
  changePercent: number;
  marketTime?: Date; // 報價時間
  name?: string; // 英文名稱 (僅作為最低優先的名稱備援)
}

class YahooFinanceService {
  private readonly baseUrl = 'https://query1.finance.yahoo.com/v8/finance/chart';
  private readonly CACHE_TTL = 60 * 1000; // 60 秒快取
  private readonly NOT_FOUND_TTL = 10 * 60 * 1000; // 查無代號的負快取 10 分鐘
  private readonly CONCURRENCY = 4; // 批次並發數
  private readonly SUFFIX_LISTED = '.TW'; // 上市
  private readonly SUFFIX_OTC = '.TWO'; // 上櫃/興櫃
  private readonly notFoundCache = new Map<string, number>(); // symbol -> 快取到期時間
  private readonly cachedQuoteKeys = new Set<string>(); // 已寫入 stockDataCache 的鍵 (供完整重設用)

  /**
   * 判斷是否為「代號不存在」(HTTP 404) 的錯誤
   * 優先使用 corsProxyService 附上的 status，字串比對僅作為舊錯誤的相容備援
   */
  private isNotFoundError(error: any): boolean {
    if (error?.status === 404) {
      return true;
    }
    const message = typeof error?.message === 'string' ? error.message : '';
    return message.includes('HTTP 404') || message.includes('Yahoo symbol not found');
  }

  private isKnownNotFound(symbol: string): boolean {
    const expiry = this.notFoundCache.get(symbol);
    if (expiry === undefined) {
      return false;
    }
    if (Date.now() > expiry) {
      this.notFoundCache.delete(symbol);
      return false;
    }
    return true;
  }

  /**
   * 依靜態資料的市場別組成 Yahoo 代號
   * 上市 → .TW；上櫃/興櫃 → .TWO；未知 → 預設 .TW (查無時自動改試 .TWO)
   */
  private buildYahooSymbol(symbol: string): { primary: string; fallback?: string } {
    const clean = symbol.trim().toUpperCase();
    let market: string | undefined;
    try {
      market = staticStockDataService.getStockInfoSync(clean)?.market;
    } catch {
      market = undefined;
    }

    if (market === '上市') {
      return { primary: `${clean}${this.SUFFIX_LISTED}`, fallback: `${clean}${this.SUFFIX_OTC}` };
    }
    if (market === '上櫃' || market === '興櫃') {
      return { primary: `${clean}${this.SUFFIX_OTC}`, fallback: `${clean}${this.SUFFIX_LISTED}` };
    }
    return { primary: `${clean}${this.SUFFIX_LISTED}`, fallback: `${clean}${this.SUFFIX_OTC}` };
  }

  /**
   * 呼叫 chart API 並解析 meta
   */
  private async fetchChartMeta(yahooSymbol: string): Promise<any | null> {
    const url = `${this.baseUrl}/${yahooSymbol}?range=1d&interval=1d`;

    const data = await RetryHelper.apiRetry(
      async () => {
        try {
          const response = await corsProxyService.fetch(url);
          return await response.json();
        } catch (error: any) {
          // 404 (代號不存在) 標記 status，讓 retry 機制不再重試
          if (this.isNotFoundError(error)) {
            const notFound: any = new Error(`Yahoo symbol not found: ${yahooSymbol}`);
            notFound.status = 404;
            throw notFound;
          }
          throw error;
        }
      },
      {
        maxRetries: 1,
        baseDelay: 2000,
        maxDelay: 10000,
      }
    );

    const result = data?.chart?.result;
    if (!Array.isArray(result) || result.length === 0 || !result[0]?.meta) {
      // 部分 CORS 代理 (如 allorigins) 會把上游 404 包成 HTTP 200，
      // 此時 Yahoo 以空 result + error 欄位表示「代號不存在」。
      // 僅將 error.code === 'Not Found' 視為 404，其他錯誤 (如暫時性失效) 不負快取
      if (data?.chart?.error?.code === 'Not Found') {
        const notFound: any = new Error(`Yahoo symbol not found: ${yahooSymbol}`);
        notFound.status = 404;
        throw notFound;
      }
      return null;
    }
    return result[0].meta;
  }

  /**
   * 呼叫 chart API，404 (代號不存在) 視為「查無」而非錯誤，供後綴 fallback 使用
   */
  private async fetchChartMetaSafe(yahooSymbol: string): Promise<{ meta: any | null; notFound: boolean }> {
    try {
      const meta = await this.fetchChartMeta(yahooSymbol);
      return { meta, notFound: false };
    } catch (error: any) {
      if (this.isNotFoundError(error)) {
        return { meta: null, notFound: true };
      }
      throw error;
    }
  }

  /**
   * 將 Yahoo meta 轉換為 YahooQuote
   */
  private parseMeta(symbol: string, yahooSymbol: string, meta: any): YahooQuote | null {
    const currentPrice = Number(meta?.regularMarketPrice);
    const previousClose = Number(meta?.chartPreviousClose ?? meta?.previousClose);

    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
      return null;
    }

    const prev = Number.isFinite(previousClose) && previousClose > 0 ? previousClose : currentPrice;
    const change = Number((currentPrice - prev).toFixed(2));
    const changePercent = prev > 0 ? Number(((change / prev) * 100).toFixed(2)) : 0;
    const marketTimeSec = Number(meta?.regularMarketTime);

    return {
      symbol,
      yahooSymbol,
      currentPrice,
      previousClose: prev,
      change,
      changePercent,
      marketTime: Number.isFinite(marketTimeSec) && marketTimeSec > 0
        ? new Date(marketTimeSec * 1000)
        : undefined,
      name: meta?.shortName || meta?.longName || undefined,
    };
  }

  /**
   * 取得單一股票報價 (含 60 秒快取、代號後綴自動修正、查無代號負快取)
   */
  async getQuote(symbol: string): Promise<YahooQuote | null> {
    const clean = symbol.trim().toUpperCase();
    if (!clean) {
      return null;
    }

    // 查無代號負快取：避免下市/未知代號在每次輪詢都重打兩個後綴
    if (this.isKnownNotFound(clean)) {
      logger.api.debug('Yahoo symbol known as not found (negative cache)', { symbol: clean });
      return null;
    }

    const cacheKey = `yahoo_quote_${clean}`;
    const cached = stockDataCache.get(cacheKey) as YahooQuote | null;
    if (cached) {
      return cached;
    }

    const { primary, fallback } = this.buildYahooSymbol(clean);

    try {
      const primaryResult = await this.fetchChartMetaSafe(primary);
      let quote = primaryResult.meta ? this.parseMeta(clean, primary, primaryResult.meta) : null;
      let notFound = primaryResult.notFound;

      // 主要後綴查無時，嘗試另一個後綴 (上市/上櫃判定錯誤時自動修正)
      if (!quote && fallback) {
        try {
          const fallbackResult = await this.fetchChartMetaSafe(fallback);
          if (fallbackResult.meta) {
            quote = this.parseMeta(clean, fallback, fallbackResult.meta);
          }
          // 兩個後綴都 404 才視為確定查無，記入負快取
          notFound = notFound && fallbackResult.notFound;
        } catch (error) {
          notFound = false;
          logger.api.debug('Yahoo fallback suffix also failed', { symbol: clean, fallback, error });
        }
      }

      if (quote) {
        stockDataCache.set(cacheKey, quote, this.CACHE_TTL);
        this.cachedQuoteKeys.add(cacheKey);
        return quote;
      }
      if (notFound) {
        this.notFoundCache.set(clean, Date.now() + this.NOT_FOUND_TTL);
      }
      return null;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'YahooFinanceService.getQuote');
      logger.api.error('Failed to fetch Yahoo quote', { symbol: clean, error: appError });
      return null;
    }
  }

  /**
   * 批次取得報價 (併發受限，單一失敗不影響其他)
   */
  async getQuotes(symbols: string[]): Promise<Map<string, YahooQuote>> {
    const results = new Map<string, YahooQuote>();
    const unique = Array.from(new Set(symbols.map((s) => s.trim().toUpperCase()))).filter(Boolean);
    if (unique.length === 0) {
      return results;
    }

    for (let i = 0; i < unique.length; i += this.CONCURRENCY) {
      const chunk = unique.slice(i, i + this.CONCURRENCY);
      const settled = await Promise.all(
        chunk.map(async (symbol) => {
          try {
            return await this.getQuote(symbol);
          } catch {
            return null; // getQuote 內部已記錄錯誤，此處確保不中斷批次
          }
        })
      );
      settled.forEach((quote, idx) => {
        if (quote) {
          results.set(chunk[idx], quote);
        }
      });
    }

    logger.api.info('Resolved Yahoo quotes for symbols', {
      requested: unique.length,
      resolved: results.size,
    });

    return results;
  }

  /**
   * 清除快取：指定代號時清除該代號；未指定時完整重設
   * (含 stockDataCache 中的正快取 yahoo_quote_* 與查無代號負快取)
   */
  clearCache(symbols?: string[]): void {
    if (symbols && symbols.length > 0) {
      symbols.forEach((symbol) => {
        const key = `yahoo_quote_${symbol.trim().toUpperCase()}`;
        stockDataCache.delete(key);
        this.cachedQuoteKeys.delete(key);
        this.notFoundCache.delete(symbol.trim().toUpperCase());
      });
    } else {
      // 完整重設：清除所有正快取與負快取
      this.cachedQuoteKeys.forEach((key) => stockDataCache.delete(key));
      this.cachedQuoteKeys.clear();
      this.notFoundCache.clear();
    }
    logger.api.info('Yahoo quote cache cleared', { scope: symbols ? `partial (${symbols.length})` : 'all' });
  }
}

export const yahooFinanceService = new YahooFinanceService();
export default yahooFinanceService;
